import {
  Box,
  Button,
  Icon,
  IconButton,
  Theme,
  Tooltip,
  Typography,
} from "@outschool/backpack";
import { faBars, faBullhorn, faChevronLeft, faList } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { LearnerRoutes } from "@outschool/routes";
import { dayjs, OUTSCHOOL_TIMEZONE } from "@outschool/time";
import { ReadOnlyBanner } from "@outschool/ui-components-classroom";
import { useLinkComponent } from "@outschool/ui-utils";
import React from "react";

import MessageTeacherButton from "../../components/MessageTeacherButton";
import * as Env from "../../Env";
import { useCurrentLearner } from "../../providers/CurrentLearnerProvider";
import { useLearnerClassroomContext } from "../../providers/LearnerClassroomProvider";
import { useLessonsNavDrawer } from "../../providers/LessonsNavDrawerProvider";
import { useIsLiveClassroomV2 } from "./layoutPredicates";

export function getClassroomHeaderContentPath(
  sectionUid: string,
  showingAnnouncements: boolean,
) {
  return showingAnnouncements
    ? LearnerRoutes.lessonsPath(sectionUid)
    : `${LearnerRoutes.classroomPath(sectionUid)}/announcements`;
}

export default function ClassroomHeaderV2({
  showingAnnouncements = false,
  showMobileLessonsMenu,
}: {
  showingAnnouncements?: boolean;
  showMobileLessonsMenu: boolean;
}) {
  const Link = useLinkComponent();
  const { t } = useTranslation("learnerApp\\LearnerClassroom");
  const currentLearner = useCurrentLearner();
  const { openNavDrawer } = useLessonsNavDrawer();
  const { loading, error, activity, section } = useLearnerClassroomContext();
  const isLiveV2 = useIsLiveClassroomV2();
  const contentNavigationLabel = showingAnnouncements
    ? t("Lessons")
    : t("Announcements");

  if (loading || error || !activity || !section) {
    return null;
  }

  const userTimeZone = currentLearner?.timeZone || OUTSCHOOL_TIMEZONE;
  const endTime = section.end_time
    ? dayjs(section.end_time).tz(userTimeZone)
    : null;

  return (
    <Box
      data-test-id="class-header-v2"
      sx={(theme: Theme) => ({
        paddingLeft: "7.2rem",
        paddingRight: "0.8rem",
        paddingY: "2.2rem",
        [theme.breakpoints.down("md")]: {
          padding: 0,
        },
      })}
    >
      {Env.IS_READ_ONLY_MODE && <ReadOnlyBanner />}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "0.8rem",
          color: "grey.900",
        }}
      >
        <Link
          to={LearnerRoutes.dashboardPath()}
          style={{
            textDecoration: "none",
            display: "flex",
            alignItems: "center",
            gap: 6,
            color: "inherit",
            marginBottom: 7,
          }}
        >
          <Icon icon={faChevronLeft} sx={{ fontSize: "1.4rem" }} />
          <Typography variant="subtitle2" sx={{ letterSpacing: "0.1px" }}>
            {t("Back to all classes")}
          </Typography>
        </Link>

        {showMobileLessonsMenu && (
          <Box
            sx={(theme: Theme) => ({
              display: "block",
              [theme.breakpoints.up("md")]: {
                display: "none",
              },
            })}
          >
            <IconButton
              icon={faBars}
              variant="text"
              onClick={openNavDrawer}
              aria-label={t("Open lessons menu")}
            />
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "0.8rem",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "0.7rem",
            minWidth: 0,
          }}
        >
          <Tooltip title={activity.title} enterDelay={300} placement="bottom">
            <Typography
              variant="h2"
              sx={(theme: Theme) => ({
                letterSpacing: "-0.4px",
                color: "grey.900",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: "3.2rem",
                [theme.breakpoints.up("md")]: {
                  fontSize: "3.2rem",
                },
              })}
            >
              {activity.title}
            </Typography>
          </Tooltip>

          {section.is_self_paced && endTime && (
            <Typography
              variant="body1"
              sx={(theme: Theme) => ({
                marginBottom: "1rem",
                color: "grey.600",
                [theme.breakpoints.down("md")]: {
                  display: "none",
                },
              })}
            >
              {t("Class access ends {{date}}", {
                date: endTime.format("MMM D, YYYY"),
              })}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: "0.8rem" }}>
          {isLiveV2 && (
            <Button
              component={Link}
              to={getClassroomHeaderContentPath(
                section.uid,
                showingAnnouncements,
              )}
              variant="outlined"
              aria-label={contentNavigationLabel}
              sx={(theme: Theme) => ({
                display: "flex",
                alignItems: "center",
                gap: "0.8rem",
                height: 48,
                whiteSpace: "nowrap",
                [theme.breakpoints.down("lg")]: {
                  minWidth: 0,
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  padding: 0,
                },
              })}
            >
              <Icon
                icon={showingAnnouncements ? faList : faBullhorn}
                sx={{ fontSize: "1.5rem" }}
              />
              <Typography
                variant="inherit"
                sx={(theme: Theme) => ({
                  [theme.breakpoints.down("lg")]: {
                    display: "none",
                  },
                })}
              >
                {contentNavigationLabel}
              </Typography>
            </Button>
          )}
          <MessageTeacherButton
            sectionUid={section.uid}
            useTabletCompactLayout={isLiveV2}
          />
        </Box>
      </Box>
    </Box>
  );
}
